<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md"
        @click.self="emit('close')"
      >
        <div class="mx-4 flex w-full max-w-xl flex-col items-center gap-5 rounded-lg bg-white p-6 shadow-lg">
          <h2 class="text-center text-xl font-semibold text-gray-800">
            {{ t('requestTypeChange.title') }}
          </h2>

          <div class="w-full">
            <label for="request-type" class="mb-1 block text-sm font-medium text-[#385660]">
              {{ t('requestTypeChange.typeLabel') }}
            </label>
            <select
              id="request-type"
              v-model="selectedType"
              class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-700 focus:ring focus:ring-blue-200"
            >
              <option disabled value="">{{ t('requestTypeChange.selectType') }}</option>
              <option v-for="option in typeOptions" :key="option.key" :value="option.key">
                {{ option.label }}
              </option>
            </select>
          </div>

          <div v-if="selectedType === 'other_type'" class="w-full">
            <label for="request-other-action" class="mb-1 block text-sm font-medium text-[#385660]">
              {{ t('requestTypeChange.subcategoryLabel') }}
            </label>
            <select
              id="request-other-action"
              v-model="selectedOtherAction"
              :disabled="loadingOptions"
              class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-700 focus:ring focus:ring-blue-200 disabled:cursor-wait disabled:bg-gray-100"
            >
              <option disabled value="">
                {{ loadingOptions ? t('requestTypeChange.loading') : t('requestTypeChange.selectSubcategory') }}
              </option>
              <option v-for="option in otherActionOptions" :key="option.key" :value="option.key">
                {{ option.label }}
              </option>
            </select>
            <p v-if="optionsError" class="mt-2 text-sm text-red-600">{{ optionsError }}</p>
          </div>

          <!-- Alta y cambio de titular exigen la dirección del suministro: se
               precarga con la de la solicitud o la del CUPS para completarla aquí. -->
          <div v-if="needsAddress" class="flex w-full flex-col gap-3">
            <p class="text-sm text-[#385660]">{{ t('requestTypeChange.addressHint') }}</p>
            <div class="flex flex-col gap-1">
              <label for="request-type-supply-address" class="text-sm font-medium text-[#385660]">
                {{ t('requestTypeChange.supplyAddress') }}
              </label>
              <input
                id="request-type-supply-address"
                v-model="address.supply_address"
                type="text"
                maxlength="255"
                class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-700 focus:ring focus:ring-blue-200"
              />
            </div>
            <div class="flex flex-col gap-3 sm:flex-row">
              <div class="flex flex-col gap-1 sm:w-1/3">
                <label for="request-type-postal-code" class="text-sm font-medium text-[#385660]">
                  {{ t('requestTypeChange.postalCode') }}
                </label>
                <input
                  id="request-type-postal-code"
                  v-model="address.postal_code"
                  type="text"
                  inputmode="numeric"
                  maxlength="5"
                  class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-700 focus:ring focus:ring-blue-200"
                />
              </div>
              <div class="flex flex-1 flex-col gap-1">
                <label for="request-type-municipality" class="text-sm font-medium text-[#385660]">
                  {{ t('requestTypeChange.municipality') }}
                </label>
                <input
                  id="request-type-municipality"
                  v-model="address.municipality"
                  type="text"
                  maxlength="255"
                  class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-700 focus:ring focus:ring-blue-200"
                />
              </div>
            </div>
          </div>

          <!-- Por qué no se puede guardar todavía. -->
          <p v-if="missingLabels.length" class="w-full text-sm text-amber-700">
            {{ t('requestTypeChange.missingFields', { fields: missingLabels.join(', ') }) }}
          </p>

          <!-- Por qué el back rechazó el cambio. -->
          <div
            v-if="errorKeys.length"
            role="alert"
            class="flex w-full flex-col gap-1 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <p v-for="key in errorKeys" :key="key">{{ t(key) }}</p>
          </div>

          <div class="flex gap-2">
            <ButtonWhite
              :disabled="!canConfirm || saving"
              @click="confirm"
              :title="saving ? t('requestTypeChange.saving') : t('requestTypeChange.save')"
            />
            <ButtonWhite @click="emit('close')" :title="t('modal.close')" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import {
  REQUEST_TYPE_KEYS,
  canSaveRequestType,
  isSupplyAddressComplete,
  missingRequestTypeFields,
  normalizeOtherActionOptions,
  requestAddressDefaults,
  requestTypePayload,
  requiresSupplyAddress,
} from '@/services/requestTypeChange';

const props = defineProps({
  isOpen: Boolean,
  currentType: { type: String, default: '' },
  currentOtherAction: { type: String, default: '' },
  // Solicitud cargada en el detalle: de ahí sale la dirección precargada.
  request: { type: Object, default: null },
  saving: Boolean,
  // Claves de `requestTypeChange.errors` con el motivo por el que el back
  // rechazó el último intento (ver `requestTypeErrorKeys`).
  errorKeys: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();

const selectedType = ref('');
const selectedOtherAction = ref('');
const configuredOtherActions = ref([]);
const loadingOptions = ref(false);
const optionsError = ref('');
const address = reactive({ supply_address: '', postal_code: '', municipality: '' });

const needsAddress = computed(() => requiresSupplyAddress(selectedType.value, props.currentType));

const MISSING_FIELD_LABELS = {
  other_action: 'requestTypeChange.subcategoryLabel',
  supply_address: 'requestTypeChange.supplyAddress',
  postal_code: 'requestTypeChange.postalCodeDigits',
  municipality: 'requestTypeChange.municipality',
};

const missingLabels = computed(() => {
  if (loadingOptions.value || optionsError.value) return [];
  return missingRequestTypeFields(selectedType.value, selectedOtherAction.value, address, needsAddress.value)
    .map((field) => t(MISSING_FIELD_LABELS[field]));
});

const typeOptions = computed(() => REQUEST_TYPE_KEYS.map((key) => ({
  key,
  label: key === 'other_type' ? t('requestTypeChange.otherType') : t(`common.requestTypes.${key}`),
})));

const otherActionOptions = computed(() => configuredOtherActions.value
  .map((option) => ({
    key: option.key,
    label: t(`common.requestTypes.${option.key}`),
  })));

const canConfirm = computed(() => canSaveRequestType(
  selectedType.value,
  selectedOtherAction.value,
  loadingOptions.value,
  Boolean(optionsError.value)
) && (!needsAddress.value || isSupplyAddressComplete(address)));

const loadOtherActions = async () => {
  loadingOptions.value = true;
  optionsError.value = '';

  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/request/type-options`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    configuredOtherActions.value = normalizeOtherActionOptions(response.data?.value?.other_actions);

    if (configuredOtherActions.value.length === 0) {
      optionsError.value = t('requestTypeChange.loadError');
    }
  } catch (error) {
    console.error('Error loading request subcategories:', error);
    configuredOtherActions.value = [];
    optionsError.value = t('requestTypeChange.loadError');
  } finally {
    loadingOptions.value = false;
  }
};

watch(() => props.isOpen, async (open) => {
  if (!open) return;

  selectedType.value = props.currentType || '';
  selectedOtherAction.value = props.currentType === 'other_type' ? (props.currentOtherAction || '') : '';
  Object.assign(address, requestAddressDefaults(props.request));
  await loadOtherActions();
});

watch(selectedType, (type) => {
  if (type !== 'other_type') selectedOtherAction.value = '';
});

const confirm = () => {
  if (!canConfirm.value || props.saving) return;

  emit('confirm', requestTypePayload(
    selectedType.value,
    selectedOtherAction.value,
    needsAddress.value ? address : null
  ));
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
